// src/errors/argument_error.ts
var ArgumentError = class _ArgumentError extends Error {
  constructor(message) {
    super(message);
    this.message = message;
    this.name = "ArgumentError";
    Object.setPrototypeOf(this, _ArgumentError.prototype);
  }
};

// src/errors/application_error.ts
var ApplicationError = class _ApplicationError extends Error {
  constructor(message) {
    super(message);
    this.message = message;
    this.name = "ApplicationError";
    Object.setPrototypeOf(this, _ApplicationError.prototype);
  }
};

// src/clamp.tsx
function clamp(value, [min, max]) {
  if (min > max) throw new Error("min can not be greater than max");
  return Math.min(max, Math.max(min, value));
}

// src/color.ts
var Color;
((Color2) => {
  const HEX_REGEX = /^#([a-f0-9]{6})([a-f0-9]{2})?$/i;
  function lightLevel(hex, amount) {
    function normalizeValue(value) {
      if (value > 255) {
        return 255;
      }
      if (value < 0) {
        return 0;
      }
      return value;
    }
    const rgb = createRgb(hex);
    const r = normalizeValue(rgb[0] + amount);
    const g = normalizeValue(rgb[1] + amount);
    const b = normalizeValue(rgb[2] + amount);
    return createHex([r, g, b, 1]);
  }
  Color2.lightLevel = lightLevel;
  function createRgb(hex) {
    if (!HEX_REGEX.test(hex)) {
      throw new Error("Tried to modify a non hex string");
    }
    const num = parseInt(hex.slice(1), 16);
    const red = num >> 16;
    const green = num >> 8 & 255;
    const blue = num & 255;
    return [red, green, blue, 1];
  }
  Color2.createRgb = createRgb;
  function createHex(rgb) {
    function section(num) {
      const str = num.toString(16).toLowerCase();
      return str.length < 2 ? `0${str}` : str;
    }
    return `#${section(rgb[0])}${section(rgb[1])}${section(rgb[2])}`;
  }
  Color2.createHex = createHex;
  function isDark(hex) {
    const rgb = createRgb(hex);
    const yiq = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1e3;
    return yiq < 128;
  }
  Color2.isDark = isDark;
  function isLight(hex) {
    return !isDark(hex);
  }
  Color2.isLight = isLight;
})(Color || (Color = {}));

// src/debounce.ts
function debounce(fn, interval) {
  let timeout;
  return function debouncer(...args) {
    const functionCall = () => fn.apply(this, args);
    clearTimeout(timeout);
    timeout = setTimeout(functionCall, interval);
  };
}

// src/debug.ts
var Debug;
((Debug2) => {
  function assert(expression, message) {
    if (!expression) {
      throw new Error(message ?? "assertion failed");
    }
    return true;
  }
  Debug2.assert = assert;
  function assertDeclared(value, message) {
    if (typeof value === "undefined" || value === null) {
      throw new Error(message ?? "assertDeclared");
    }
    return true;
  }
  Debug2.assertDeclared = assertDeclared;
})(Debug || (Debug = {}));

// src/timer.ts
var Timer = class {
  disposers = /* @__PURE__ */ new Map();
  registeredId = 0;
  wait(callback, duration) {
    const timeout = setTimeout(callback, duration);
    const disposer = () => {
      clearTimeout(timeout);
    };
    return this.registerDisposer(disposer);
  }
  repeat(callback, duration, callOnceOnInvoke = false) {
    if (callOnceOnInvoke) {
      callback();
    }
    const interval = setInterval(callback, duration);
    const disposer = () => {
      clearInterval(interval);
    };
    return this.registerDisposer(disposer);
  }
  flush() {
    this.disposers.forEach((disposer) => disposer());
  }
  registerDisposer(disposer) {
    const localUid = this.registeredId++;
    this.disposers.set(localUid, disposer);
    return () => {
      this.disposers.delete(localUid);
      disposer();
    };
  }
};
((Timer2) => {
  function wait(...args) {
    const time = new Timer2();
    time.wait(...args);
    return time.flush.bind(time);
  }
  Timer2.wait = wait;
  function repeat(...args) {
    const time = new Timer2();
    time.repeat(...args);
    return time.flush.bind(time);
  }
  Timer2.repeat = repeat;
})(Timer || (Timer = {}));

// src/delay.tsx
function delay(delayTime) {
  return new Promise((resolve) => Timer.wait(resolve, delayTime));
}

// src/event_emitter.ts
var EventEmitter = class {
  events = /* @__PURE__ */ Object.create(null);
  errorHandlers = /* @__PURE__ */ new WeakMap();
  constructor() {
  }
  on(event, handler, errorHandler) {
    (this.events[event] || (this.events[event] = [])).push(handler);
    if (errorHandler) {
      this.errorHandlers.set(handler, errorHandler);
    }
    return () => {
      this.off(event, handler);
    };
  }
  /**
   * Un-registers handlers for events emitted by this instance.
   */
  off(event, handler) {
    if (!this.events[event]) return;
    this.events[event].splice(this.events[event].indexOf(handler) >>> 0, 1);
  }
  /**
   * Emits an event containing a given value.
   */
  emit(event, data) {
    (this.events[event] || []).forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        const errorHandler = this.errorHandlers.get(handler);
        if (errorHandler) errorHandler(error);
      }
    });
  }
};

// src/get_cookie.tsx
function getCookie(name) {
  if (document.cookie.length > 0) {
    let start = document.cookie.indexOf(name + "=");
    if (start !== -1) {
      start = start + name.length + 1;
      let end = document.cookie.indexOf(";", start);
      if (end === -1) {
        end = document.cookie.length;
      }
      return unescape(document.cookie.substring(start, end));
    }
  }
  return null;
}

// src/is_visiba_ios_app.tsx
function isVisibaIOSApp() {
  try {
    const cookie = getCookie("VisibaIOSApp");
    return !!cookie;
  } catch {
    return false;
  }
}

// src/get_platform.tsx
var DeviceType = /* @__PURE__ */ ((DeviceType2) => {
  DeviceType2[DeviceType2["Ios"] = 0] = "Ios";
  DeviceType2[DeviceType2["Android"] = 1] = "Android";
  DeviceType2[DeviceType2["Web"] = 2] = "Web";
  return DeviceType2;
})(DeviceType || {});
function getPlatform() {
  const userAgent = navigator.userAgent || navigator.vendor;
  if (/android/i.test(userAgent)) {
    return 1 /* Android */;
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream || isVisibaIOSApp()) {
    return 0 /* Ios */;
  }
  return 2 /* Web */;
}

// src/platform.tsx
var Platform = class {
  static userAgentData() {
    return this.parseAgentData();
  }
  static browserIs(test) {
    const statements = [];
    const agent = this.userAgentData();
    if ((test & 1 /* Edge */) === 1 /* Edge */) {
      statements.push(agent.browsers.edge);
    }
    if ((test & 32 /* SamsungBrowser */) === 32 /* SamsungBrowser */) {
      statements.push(agent.browsers.samsungBrowser);
    }
    if ((test & 64 /* Opera */) === 64 /* Opera */) {
      statements.push(agent.browsers.opera);
    }
    if ((test & 8 /* Firefox */) === 8 /* Firefox */) {
      statements.push(agent.browsers.firefox);
    }
    if ((test & 16 /* Chrome */) === 16 /* Chrome */) {
      statements.push(agent.browsers.chrome);
    }
    if ((test & 2 /* Safari */) === 2 /* Safari */) {
      statements.push(agent.browsers.safari);
    }
    if ((test & 4 /* StrictSafari */) === 4 /* StrictSafari */) {
      statements.push(agent.browsers.strictSafari);
    }
    if ((test & 128 /* IE */) === 128 /* IE */) {
      statements.push(agent.browsers.ie);
    }
    if ((test & 256 /* EdgeChromium */) === 256 /* EdgeChromium */) {
      statements.push(agent.browsers.edgeChromium);
    }
    if ((test & 512 /* EdgeAndroid */) === 512 /* EdgeAndroid */) {
      statements.push(agent.browsers.edgeAndroid);
    }
    if ((test & 1024 /* EdgeIos */) === 1024 /* EdgeIos */) {
      statements.push(agent.browsers.edgeIos);
    }
    if ((test & 2048 /* FirefoxIos */) === 2048 /* FirefoxIos */) {
      statements.push(agent.browsers.firefoxIos);
    }
    if ((test & 4096 /* ChromeIos */) === 4096 /* ChromeIos */) {
      statements.push(agent.browsers.chromeIos);
    }
    return statements.filter(Boolean).length > 0;
  }
  static platformIs(test) {
    const statements = [];
    const agent = this.userAgentData();
    if ((test & 1 /* Ios */) === 1 /* Ios */) {
      statements.push(agent.platform.ios);
    }
    if ((test & 2 /* Android */) === 2 /* Android */) {
      statements.push(agent.platform.android);
    }
    if ((test & 4 /* Mobile */) === 4 /* Mobile */) {
      statements.push(agent.platform.mobile);
    }
    if ((test & 8 /* Desktop */) === 8 /* Desktop */) {
      statements.push(agent.platform.desktop);
    }
    return statements.filter(Boolean).length > 0;
  }
  static runtimeSupportsTouch() {
    return "ontouchstart" in window || window.navigator.maxTouchPoints > 0;
  }
  static runtimeIsInBrowser() {
    return typeof navigator !== "undefined" && typeof window !== "undefined";
  }
  static parseAgentData() {
    const edgeHTML = this.runtimeIsInBrowser() && navigator.userAgent.includes("Edge/");
    const edgeChromium = this.runtimeIsInBrowser() && navigator.userAgent.includes("Edg/");
    const edgeAndroid = this.runtimeIsInBrowser() && navigator.userAgent.includes("EdgA/");
    const edgeIos = this.runtimeIsInBrowser() && navigator.userAgent.includes("EdgiOS/");
    const edge = edgeHTML || edgeChromium || edgeAndroid || edgeIos;
    const samsungBrowser = this.runtimeIsInBrowser() && navigator.userAgent.includes("SamsungBrowser/");
    const opera = this.runtimeIsInBrowser() && (navigator.userAgent.includes("Opera") || navigator.userAgent.includes("OPR/"));
    const operaIos = this.runtimeIsInBrowser() && navigator.userAgent.includes("OPiOS/");
    const firefoxIos = this.runtimeIsInBrowser() && navigator.userAgent.includes("FxiOS/");
    const firefox = this.runtimeIsInBrowser() && (navigator.userAgent.includes("Firefox") || firefoxIos);
    const chromeIos = this.runtimeIsInBrowser() && navigator.userAgent.includes("CriOS/");
    const chrome = this.runtimeIsInBrowser() && (navigator.userAgent.includes("Chrome") || chromeIos) && !edge && !opera && !samsungBrowser;
    const safari = this.runtimeIsInBrowser() && navigator.userAgent.includes("Safari") && !chrome && !edgeChromium && !firefox && !opera && !samsungBrowser || edgeIos || chromeIos || firefoxIos;
    const strictSafari = this.runtimeIsInBrowser() && navigator.userAgent.includes("Safari") && !chrome && !edge && !firefox && !opera && !samsungBrowser;
    const ie = this.runtimeIsInBrowser() && navigator.userAgent.includes("Trident");
    const visibaIOSApp = isVisibaIOSApp();
    const browsers = Object.freeze({
      edgeChromium,
      edgeAndroid,
      edgeIos,
      edge,
      firefoxIos,
      chromeIos,
      samsungBrowser,
      opera,
      operaIos,
      firefox,
      chrome,
      safari,
      strictSafari,
      ie,
      visibaIOSApp
    });
    const ios = this.runtimeIsInBrowser() && (/iPad|iPhone|iPod/.test(navigator.userAgent) || isVisibaIOSApp());
    const android = this.runtimeIsInBrowser() && /Android/.test(navigator.userAgent);
    const mobile = ios || android;
    const desktop = !mobile;
    const platform2 = Object.freeze({
      ios,
      android,
      mobile,
      desktop
    });
    return Object.freeze({
      browsers,
      platform: platform2
    });
  }
};
var BrowserList = /* @__PURE__ */ ((BrowserList2) => {
  BrowserList2[BrowserList2["None"] = 0] = "None";
  BrowserList2[BrowserList2["Edge"] = 1] = "Edge";
  BrowserList2[BrowserList2["Safari"] = 2] = "Safari";
  BrowserList2[BrowserList2["StrictSafari"] = 4] = "StrictSafari";
  BrowserList2[BrowserList2["Firefox"] = 8] = "Firefox";
  BrowserList2[BrowserList2["Chrome"] = 16] = "Chrome";
  BrowserList2[BrowserList2["SamsungBrowser"] = 32] = "SamsungBrowser";
  BrowserList2[BrowserList2["Opera"] = 64] = "Opera";
  BrowserList2[BrowserList2["IE"] = 128] = "IE";
  BrowserList2[BrowserList2["EdgeChromium"] = 256] = "EdgeChromium";
  BrowserList2[BrowserList2["EdgeAndroid"] = 512] = "EdgeAndroid";
  BrowserList2[BrowserList2["EdgeIos"] = 1024] = "EdgeIos";
  BrowserList2[BrowserList2["FirefoxIos"] = 2048] = "FirefoxIos";
  BrowserList2[BrowserList2["ChromeIos"] = 4096] = "ChromeIos";
  BrowserList2[BrowserList2["OperaIos"] = 8192] = "OperaIos";
  BrowserList2[BrowserList2["visibaIOSApp"] = 16384] = "visibaIOSApp";
  return BrowserList2;
})(BrowserList || {});
var PlatformList = /* @__PURE__ */ ((PlatformList2) => {
  PlatformList2[PlatformList2["None"] = 0] = "None";
  PlatformList2[PlatformList2["Ios"] = 1] = "Ios";
  PlatformList2[PlatformList2["Android"] = 2] = "Android";
  PlatformList2[PlatformList2["Mobile"] = 4] = "Mobile";
  PlatformList2[PlatformList2["Desktop"] = 8] = "Desktop";
  return PlatformList2;
})(PlatformList || {});
var platform = Platform;

// src/parameter_handler.ts
var ParameterHandler = class {
  /**
   * Map of current parameters.
   * */
  parameters = /* @__PURE__ */ Object.create(null);
  /** Appends a new object of parameters */
  append(item) {
    Object.assign(this.parameters, item);
  }
  /** Clears the parameter maps. */
  clearParameters() {
    this.parameters = /* @__PURE__ */ Object.create(null);
  }
  /** Returns a query string based on the values in the map. */
  getString() {
    if (Object.keys(this.parameters).length === 0) return "";
    const entries = Object.entries(this.parameters);
    return entries.reduce((accumulator, currentValue, currentIndex) => {
      let combine = accumulator + currentValue.join("=");
      if (entries.length - 1 !== currentIndex) {
        combine += "&";
      }
      return combine;
    }, "?");
  }
};

// src/prefers_reduced_motion.tsx
function prefersReducedMotion() {
  return globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// src/rem.ts
function rem(px) {
  return `${px / 16}rem`;
}
function remToNumber(remValue) {
  const strippedRem = remValue.replace("rem", "");
  const value = parseInt(strippedRem, 10);
  if (isNaN(value)) {
    return 16;
  }
  return value * 16;
}

// src/retry.ts
var Retry;
((Retry2) => {
  Retry2.byBruteForce = (fn, options) => {
    const defaultOption = {
      every: 1e3,
      for: 1,
      hooks: [],
      ...options ? options : {}
    };
    return new Promise((resolve, reject) => {
      let attempts = 0;
      let finishedRequest = false;
      const disposer = Timer.repeat(
        () => {
          proxy(++attempts);
        },
        defaultOption.every,
        true
      );
      async function proxy(attempt) {
        if (attempt > defaultOption.for && !finishedRequest) {
          disposer();
          reject(`request did not finish in ${defaultOption.for} attempts`);
        } else {
          try {
            const resolvedIntoThis = await fn();
            if (!finishedRequest) {
              disposer();
              finishedRequest = true;
              resolve(resolvedIntoThis);
            }
          } catch (error) {
            defaultOption.hooks?.forEach((hook) => hook());
          }
        }
      }
    });
  };
  Retry2.the = (fn, options) => {
    const defaultOption = {
      againAfter: 1e3,
      for: 1,
      hooks: [],
      ...options ? options : {}
    };
    return new Promise((resolve, reject) => {
      let attempts = 0;
      async function proxy(attempt) {
        try {
          const resolvedIntoThis = await fn();
          resolve(resolvedIntoThis);
        } catch (error) {
          defaultOption.hooks?.forEach((hook) => hook());
          if (attempt < defaultOption.for) {
            retry();
          } else {
            reject(`request did not finish in ${defaultOption.for} attempts`);
          }
        }
      }
      function retry() {
        setTimeout(() => {
          proxy(++attempts);
        }, defaultOption.againAfter);
      }
      proxy(++attempts);
    });
  };
})(Retry || (Retry = {}));

// src/safe_json_parse.ts
function safeJsonParse(data) {
  try {
    return JSON.parse(data);
  } catch (error) {
    return data;
  }
}

// src/storage.tsx
function storage(spec = {}) {
  const storeType = spec.type ?? "local";
  const store = storeType === "local" ? globalThis.localStorage : globalThis.sessionStorage;
  return {
    storeType,
    get(key) {
      const item = store.getItem(`${spec.prefix ?? ""}${key.toString()}`);
      if (!item) return null;
      return safeJsonParse(item);
    },
    set(key, data) {
      store.setItem(`${spec.prefix ?? ""}${key.toString()}`, JSON.stringify(data));
    },
    removeItem(key) {
      store.removeItem(`${spec.prefix ?? ""}${key.toString()}`);
    }
  };
}

// src/vector.ts
var Vec2 = class {
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
  getAsObj() {
    return {
      x: this.x,
      y: this.y
    };
  }
};
var Vec3 = class {
  constructor(x, y, z) {
    this.x = x;
    this.y = y;
    this.z = z;
  }
  getAsObj() {
    return {
      x: this.x,
      y: this.y,
      z: this.z
    };
  }
};

// src/create_initials.ts
function createInitials(commonName, separator) {
  const parts = commonName.trim().split(" ");
  if (parts.length === 0) {
    return "";
  }
  const firstPart = parts[0].charAt(0).toUpperCase();
  if (parts.length === 1) {
    return firstPart;
  }
  const secondPart = (parts[parts.length - 1] ?? "").charAt(0).toUpperCase();
  return firstPart + (separator ?? "") + secondPart;
}

// src/format_bytes.ts
function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";
  const base = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const sizeIndex = Math.floor(Math.log(bytes) / Math.log(base));
  return (bytes / Math.pow(base, sizeIndex)).toFixed(dm) + " " + sizes[sizeIndex];
}

// src/convert_to_milliseconds.tsx
function convertToMilliseconds(hours, minutes, seconds) {
  return (hours * 60 * 60 + minutes * 60 + seconds) * 1e3;
}

// src/is_some_error.tsx
function isSomeError(value) {
  return value instanceof Error || value instanceof DOMException || value instanceof AggregateError || value instanceof EvalError || value instanceof RangeError || value instanceof ReferenceError || value instanceof SyntaxError || value instanceof TypeError || value instanceof URIError;
}

// src/safe_number_parse.ts
function safeNumberParse(input) {
  if (input == null || input.trim() === "") return null;
  const parsed = Number(input);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

// src/type_checks.tsx
function isResultType(value) {
  if (typeof value === "object" && !Array.isArray(value) && value !== null && "ok" in value && "err" in value) {
    return true;
  }
  return false;
}
function isPromise(value) {
  return typeof value === "object" && !Array.isArray(value) && value !== null && "then" in value;
}
function isBoolean(value) {
  return typeof value === "boolean";
}
function isString(value) {
  return typeof value === "string";
}
function isError(value) {
  return value instanceof Error;
}
function isFunction(value) {
  return typeof value === "function";
}

export { ApplicationError, ArgumentError, BrowserList, Color, Debug, DeviceType, EventEmitter, ParameterHandler, PlatformList, Retry, Timer, Vec2, Vec3, clamp, convertToMilliseconds, createInitials, debounce, delay, formatBytes, getCookie, getPlatform, isBoolean, isError, isFunction, isPromise, isResultType, isSomeError, isString, isVisibaIOSApp, platform, prefersReducedMotion, rem, remToNumber, safeJsonParse, safeNumberParse, storage };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map